import React, { useLayoutEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { StudentForm } from './components/StudentForm';
import { AdvisorChatBox } from './components/AdvisorChatBox';
import { ChatInput } from '@components/ChatInput';
import { FeedbackForm } from './components/FeedbackForm';
import { advisorApiService } from './services/advisor-api';
import { useAuth } from './contexts/AuthContext';
import { ADMIN_AUTH_ROUTES } from './constants/routes';
import type { AdvisorChatMessage, StudentInfo } from './types';
import './styles/App.css';

const CucsAdvisingApp: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [studentInfo, setStudentInfo] = useState<StudentInfo | null>(null);
  const [messages, setMessages] = useState<AdvisorChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [currentQuestionId, setCurrentQuestionId] = useState<string | undefined>();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  // 'metlas-strict' is reserved as a third mode (Metlas FAQ retrieval with no AI
  // fallback). It has no UI affordance yet — the toggle below only flips between
  // 'metlas-off' and 'metlas'. To enter strict mode, set the initial state here.
  type MetlasMode = 'metlas-off' | 'metlas' | 'metlas-strict';
  const [metlasMode, setMetlasMode] = useState<MetlasMode>('metlas-strict');
  const metlasEnabled = metlasMode !== 'metlas-off';
  const aiGenerationEnabled = metlasMode !== 'metlas-strict';
  const isAdmin = user?.role === 'admin';

  // Mobile browsers without `dvh` support size 100vh past the URL bar, pushing
  // the chat input off-screen. Track the real visible height instead; this also
  // keeps the input above the on-screen keyboard while typing.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const setAppHeight = () => {
      const height = viewport?.height ?? window.innerHeight;
      root.style.setProperty('--app-height', `${height}px`);
      // When the keyboard opens, iOS Safari also scrolls the page to reveal the
      // focused input. The app is already resized to the visible area, so that
      // scroll only slides it off the top and exposes blank space — pin it back.
      if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
    };
    setAppHeight();
    viewport?.addEventListener('resize', setAppHeight);
    viewport?.addEventListener('scroll', setAppHeight);
    window.addEventListener('resize', setAppHeight);
    return () => {
      viewport?.removeEventListener('resize', setAppHeight);
      viewport?.removeEventListener('scroll', setAppHeight);
      window.removeEventListener('resize', setAppHeight);
      root.style.removeProperty('--app-height');
    };
  }, []);

  const handleStudentInfoChange = (newInfo: StudentInfo) => {
    setStudentInfo(newInfo);
    setMessages([]);
    setCurrentQuestionId(undefined);
    setShowSettings(false);
  };

  const handleSendMessage = async (message: string) => {
    if (!studentInfo) return;

    // Build history from existing messages before adding the new one (sliding window: last 6)
    const history = messages.slice(-6).map((m) => ({
      role: (m.type === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
      content: m.text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
    }));

    const userMessage: AdvisorChatMessage = {
      type: 'user',
      text: message,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setCurrentQuestionId(undefined);

    try {
      const response = metlasEnabled
        ? await advisorApiService.askQuestion(message, studentInfo, history, aiGenerationEnabled)
        : await advisorApiService.askQuestionDirect(message, studentInfo, history);
      if (response.data) {
        const sourceUriMatch = response.data.message.match(/Source:\s*(s3:\/\/[^\s<]+)/);
        const assistantMessage: AdvisorChatMessage = {
          type: 'assistant',
          text: response.data.message,
          timestamp: new Date(),
          id: response.data.id,
          sourceUri: sourceUriMatch ? sourceUriMatch[1] : undefined,
          metadata: response.data.metadata,
          score: response.data.score,
          flagged: response.data.flagged,
          ai_generated: response.data.ai_generated,
          ai_warning: response.data.ai_warning,
          citations: response.data.citations,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        setCurrentQuestionId(response.data.id);
      } else {
        throw new Error(response.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      let errorText = 'Unknown error';
      let statusCode: number | undefined;
      let flagged = false;
      let questionId: string | undefined;

      if (error instanceof Error) errorText = error.message;
      if (axios.isAxiosError(error)) {
        const axiosError = error;
        statusCode = axiosError.response?.status;
        if (axiosError.response?.data?.error) errorText = axiosError.response.data.error;
        if (axiosError.response?.data?.data) {
          if (axiosError.response.data.data.flagged !== undefined) flagged = axiosError.response.data.data.flagged;
          if (axiosError.response.data.data.id) questionId = axiosError.response.data.data.id;
        }
      }

      const isServerError = statusCode !== undefined && statusCode >= 500 && statusCode < 600;
      const isPermissionError = statusCode === 403;
      const formattedText =
        isServerError || isPermissionError
          ? `<div class='error-box'><h3>Error</h3><p>${errorText}</p></div>`
          : errorText;

      const errorMessage: AdvisorChatMessage = {
        type: 'assistant',
        text: formattedText,
        timestamp: new Date(),
        flagged,
        id: questionId,
      };
      setMessages((prev) => [...prev, errorMessage]);
      if (questionId) setCurrentQuestionId(questionId);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRateQuality = async (isRelevant: boolean, notes?: string) => {
    if (!currentQuestionId) return;
    try {
      await advisorApiService.rateQuality(currentQuestionId, isRelevant, notes);
      setMessages((prev) =>
        prev.map((msg) => (msg.id === currentQuestionId ? { ...msg, isRelevant, researcherNotes: notes } : msg))
      );
    } catch (error) {
      console.error('Failed to rate quality:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(ADMIN_AUTH_ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleClearHistory = () => {
    setMessages([]);
    setCurrentQuestionId(undefined);
  };

  return (
    <div className="research-tool">
      <header className="tool-header">
        <div className="header-content">
          <div className="header-title">
            <img src="/columbia_university_head.png" alt="Columbia University" className="columbia-icon" />
            <div>
              <h1>Student Advising Chatbot at Columbia University Computer Science Department</h1>
              <p className="header-subtitle">Chat with Columbia's smartest lion</p>
            </div>
          </div>
        </div>
        {user && (
          <div className="header-user-info">
            {isAdmin && (
              <>
                <button
                  className={`metlas-toggle-btn${metlasEnabled ? ' metlas-toggle-on' : ' metlas-toggle-off'}`}
                  onClick={() => setMetlasMode((m) => (m === 'metlas-off' ? 'metlas' : 'metlas-off'))}
                  type="button"
                  title={
                    metlasEnabled
                      ? 'Metlas is on — click to switch to Metlas-off'
                      : 'Metlas is off — click to switch to Metlas'
                  }
                  aria-pressed={metlasEnabled}
                >
                  <span className="metlas-toggle-track">
                    <span className="metlas-toggle-knob" />
                  </span>
                  Metlas
                </button>
                <span className="header-user-separator" aria-hidden="true">
                  |
                </span>
              </>
            )}
            <span className="header-username">{user.username}</span>
            <span className="header-user-separator" aria-hidden="true">
              |
            </span>
            <button className="logout-button" onClick={() => { void handleLogout(); }} type="button">
              Logout
            </button>
          </div>
        )}
      </header>

      {showSettings && (
        <>
          <div className="settings-backdrop" onClick={() => { if (studentInfo) setShowSettings(false); }} />
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h3>{studentInfo ? 'Student Profile' : 'Select your student profile'}</h3>
              {studentInfo && (
                <button className="settings-close-btn" onClick={() => setShowSettings(false)} type="button" aria-label="Close">✕</button>
              )}
            </div>
            <div className="settings-panel-body">
              <StudentForm
                onSubmit={async (info) => handleStudentInfoChange(info)}
                initialValues={studentInfo || undefined}
              />
            </div>
          </div>
        </>
      )}

      <div className="main-content chat-layout">
          <aside className="guide-sidebar">
            <div className="sidebar-section">
              <div className="sidebar-label">Active Profile</div>
              {studentInfo ? (
              <div className="sidebar-profile">
                <div className="spr"><span>Status</span><strong>{studentInfo.status}</strong></div>
                <div className="spr"><span>Type</span><strong>{studentInfo.student_type}</strong></div>
                <div className="spr"><span>School</span><strong>{studentInfo.school}</strong></div>
                <div className="spr"><span>Program</span><strong>{studentInfo.program}</strong></div>
                <div className="spr"><span>Visa</span><strong>{studentInfo.visa_restriction}</strong></div>
              </div>
              ) : (
              <div className="sidebar-no-profile">No profile set</div>
              )}
              <button className="settings-button" onClick={() => setShowSettings(!showSettings)} type="button">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:'5px'}}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                Update
              </button>
            </div>
            <div className="sidebar-section">
              <div className="sidebar-label">How to Use</div>
              <ul className="sidebar-tips">
                <li>Always check if the bot understands your question</li>
                <li>Rate the answer with&nbsp;
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle'}}><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                  &nbsp;or&nbsp;
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle'}}><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
                </li>
              </ul>
            </div>
            <div className="sidebar-footer">
              <button className="sidebar-clear-btn" onClick={handleClearHistory} type="button">
                🗑 Clear chat
              </button>
            </div>
          </aside>

          <div className="chat-column">
            <div className="chat-area">
              <AdvisorChatBox
                messages={messages}
                currentQuestionId={currentQuestionId}
                onRateQuality={handleRateQuality}
                onOpenFeedback={() => setShowFeedbackForm(true)}
              />
            </div>
            <ChatInput onSendMessage={handleSendMessage} isLoading={isLoading || !studentInfo} />
          </div>
      </div>

      <FeedbackForm
        isOpen={showFeedbackForm}
        onClose={() => setShowFeedbackForm(false)}
        studentInfo={studentInfo}
      />
    </div>
  );
};

export default CucsAdvisingApp;
